import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  LogarithmicScale
} from 'chart.js';
import { Line } from 'react-chartjs-2'; 
import { monthsInOrder, unitsClass } from '../../helpers/infoFilter';
import { normalizeText } from "../../helpers/functions";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  LogarithmicScale
);

const applyLogTransformation = (data) => {
  return data.map(value => Math.log10(value + 1));
};

const LineChartConsumption = ({data, type, filterClass = []}) => {

const utilityColors = {
    "ELECTRICIDAD": '#DA5B5B',
    "Energía Consumida": "#E09C4A",
    "Energia Consumida": "#E09C4A",
    "Energia Reactiva": "#C2C94C",
    "Energía Reactiva": "#C2C94C",
    "Potencia Excedida": "#66A060",
    "Potencia Contratada": "#7A9CB0",
    "Potencia Demandada": "#4D6FA9",
    "Energia Inyectada": "#8C6CC3",
    "Potencia Renovable": "#B66FB1",
    "Energia Renovable": "#9C5D70",
    "Energia Autogenerada": "#A6845E",
    "Coseno fi": "#8B8B8B",
    "Tangente fi": "#6F6F91",
    "GAS NATURAL": '#eaea59',
    "Gas Cons. a 9300 Kcal/m3": "#eaea59",
    "Gas Consumido": "#eaea89",
    "AGUA": '#63b670',
    "Agua Consumida": "#63b670"
  };
  
  const dataMemo = useMemo(() => {
    let usagePerMonth = {};
    let labels = [];
    let allUtilities = new Set();
    if (Array.isArray(data)) {
      if(type === "finance"){
        data?.forEach((item) => {
          if(item?.lineItemText !== "Total Factura"){
            let cost = parseFloat(item?.cost);
            if (!isNaN(cost)) {
              let date = new Date(Date.parse(item?.dueDate));
              let monthYear = date.toLocaleString('es-ES', { month: 'long' }) + '-' + date?.getFullYear();
              if (!usagePerMonth[monthYear]) {
                usagePerMonth[monthYear] = {ELECTRICIDAD: 0, "GAS NATURAL": 0, AGUA: 0};
                labels.push(monthYear);
              }
              if (item?.utilityType in usagePerMonth[monthYear]) {
                usagePerMonth[monthYear][item.utilityType] += cost;
                allUtilities.add(item?.utilityType);
              }
            }
          }
        });
      } else {
        if(type === "consumption" && filterClass.length > 0){
          data?.forEach((item) => {
            if (item?.lineItemText !== "Total Factura") {
              let consumption = parseFloat(item?.consumption);
              if (!isNaN(consumption)) {
                let date = new Date(Date.parse(item?.dueDate));
                let monthYear = date.toLocaleString('es-ES', { month: 'long' }) + '-' + date?.getFullYear();
                if (!usagePerMonth[monthYear]) {
                  usagePerMonth[monthYear] = {};
                  labels.push(monthYear);
                }
                let classType = normalizeText(item?.class);
                if (!usagePerMonth[monthYear][classType]) {
                  usagePerMonth[monthYear][classType] = 0;
                }
                usagePerMonth[monthYear][classType] += consumption;
                allUtilities.add(classType);
              }
            }
          });
        } else {
          data?.forEach((item) => {
            if(item?.lineItemText !== "Total Factura"){
              let consumption = parseFloat(item?.consumption);
              if (!isNaN(consumption)) {
                let date = new Date(Date.parse(item?.dueDate));
                let monthYear = date.toLocaleString('es-ES', { month: 'long' }) + '-' + date?.getFullYear();
                if (!usagePerMonth[monthYear]) {
                  usagePerMonth[monthYear] = {ELECTRICIDAD: 0, "GAS NATURAL": 0, AGUA: 0};
                  labels.push(monthYear);
                }
                if (item?.utilityType in usagePerMonth[monthYear]) {
                  usagePerMonth[monthYear][item.utilityType] += consumption;
                  allUtilities.add(item?.utilityType);
                }
              }
            }
          });
        }
      }
    }

    labels?.sort((a, b) => {
      const [monthA, yearA] = a.split('-');
      const [monthB, yearB] = b.split('-');
      if (yearA !== yearB) {
        return yearA - yearB;
      } else {
        return monthsInOrder.indexOf(monthA) - monthsInOrder.indexOf(monthB);
      }
    });

    labels?.forEach(monthYear => {
      for (let utility in usagePerMonth[monthYear]) {
        if (usagePerMonth[monthYear][utility] === undefined) {
          usagePerMonth[monthYear][utility] = 0;
        }
      }
    });

    let datasets = [];
    allUtilities?.forEach(utility => {
      let data = labels?.map(monthYear => usagePerMonth[monthYear][utility]);
      data = applyLogTransformation(data);
      datasets.push({
        label: type === "finance" ? utility + ' AR$' : utility,
        data: data,
        fill: false,
        tension: 0.3,
        borderColor: utilityColors[utility],
        backgroundColor: utilityColors[utility],
        pointRadius: 3,
        pointHoverRadius: 5
      });
    });

    if(type === "consumption" && filterClass?.length > 0){
      const order = ["Gas Consumido", "Gas Cons. a 9300 Kcal/m3", "Agua Consumida"];
      datasets.sort((a, b) => {
        const indexA = order.indexOf(a.label);
        const indexB = order.indexOf(b.label);
        if (indexA === -1 && indexB === -1) return 0;
        else if (indexA === -1) return 1;
        else if (indexB === -1) return -1;
        else return indexA - indexB;
      });
    }

    return {
      labels: labels,
      datasets: datasets,
    };
  }, [data]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    aspectRatio: undefined,
    scales: {
      x: { beginAtZero: true },
      y: {
        beginAtZero: true,
        ticks: {
          callback: function (value) {
            const realValue = Math.pow(10, value);
            if (Number.isInteger(value)) {
              return realValue.toLocaleString('es-AR');
            }
            return '';
          }
        }
      }
    },
    plugins: {
      legend: { display: true },
      title: {
        display: true,
        text: type === "finance"
          ? 'Gastos de Electricidad, Agua y Gas'
          : 'Consumos de Electricidad, Agua y Gas'
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            const unit = (label === "GAS NATURAL" || label === "AGUA") ? "m3" : (label === "ELECTRICIDAD" && filterClass?.length === 0) ? "kWh" : unitsClass[label] || '';
            if (label) {
              label = type === "finance" ? `${label}: ` : `Consumo de ${label}: `;
            }
            const originalValue = Math.pow(10, context.parsed.y) - 1;
            const formattedValue = originalValue?.toLocaleString('es-AR', {minimumFractionDigits: 2, maximumFractionDigits: 2});
            return `${label} ${formattedValue} ${type === "consumption" ? unit : ""}`;
          }
        }
      }
    }
  };

  return (
    <div style={{ height: '250px', width: '100%' }}>
      <Line data={dataMemo} options={options} />
    </div>
  );
};

export default LineChartConsumption;
